import React from 'react';
import { useTranslation } from 'react-i18next';
import ErrorComponent from '@components-clean/core/error';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { COMPONENT_NAME as AUTOMATION_OPTIONS_COMPONENT_NAME } from '@pages-clean/central-monitoring/dataset-hub/components/automation-options-card';
import DatasetContent from '@pages-clean/central-monitoring/dataset-hub/tabs';
import {
    controlsAutomationColumns,
    siteAutomationColumns,
} from '@pages-clean/central-monitoring/dataset-hub/tabs/automation/column-list';
import { datasetInfoColumns } from '@pages-clean/central-monitoring/dataset-hub/tabs/dataset-info/column-list';
import { COMPONENT_NAME as DATASET_NOTES_COMPONENT_NAME } from '@pages-clean/central-monitoring/dataset-hub/tabs/dataset-info/dataset-notes';
import { useAuthorization, useLocalState } from '@hooks/index';
import './index.scss';

interface IContentProps {
    studyRef: string;
    studyNumber: string;
}

const Content = ({ studyRef, studyNumber }: IContentProps) => {
    const user = useAuthorization();
    if (!user) throw new Error('User not found');
    const { t } = useTranslation('cm', {
        keyPrefix: 'datasetHub',
    });

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    enum Tabs {
        DataSetInfo,
        SiteAutomation,
        ControlsAutomation,
    }

    return (
        <TabStripLowDom
            data-testid="tabs"
            data={[
                {
                    title: t('tabs.datasetInfo.title'),
                    dataTestId: 'dataset-info-tab',
                    content: (
                        <DatasetContent
                            studyRef={studyRef}
                            studyNumber={studyNumber}
                            selectedTab={Tabs.DataSetInfo}
                            columns={datasetInfoColumns(t)}
                            key={Tabs.DataSetInfo}
                        />
                    ),
                },
                {
                    title: t('tabs.automation.title'),
                    dataTestId: 'site-automation-tab',
                    content: (
                        <DatasetContent
                            studyRef={studyRef}
                            studyNumber={studyNumber}
                            selectedTab={Tabs.SiteAutomation}
                            columns={siteAutomationColumns(t)}
                            key={Tabs.SiteAutomation}
                        />
                    ),
                },
                {
                    title: t('tabs.controlsAutomation.title'),
                    dataTestId: 'controls-automation-tab',
                    content: (
                        <DatasetContent
                            studyRef={studyRef}
                            studyNumber={studyNumber}
                            selectedTab={Tabs.ControlsAutomation}
                            columns={controlsAutomationColumns(t)}
                            key={Tabs.ControlsAutomation}
                        />
                    ),
                },
            ]}
            canChangeTab={async () => {
                if (componentHasChanges(DATASET_NOTES_COMPONENT_NAME)) {
                    const confirmed = await confirmDiscardChanges();
                    if (!confirmed) {
                        return false;
                    }
                    setComponentHasChanges(DATASET_NOTES_COMPONENT_NAME, false);
                }
                if (componentHasChanges(AUTOMATION_OPTIONS_COMPONENT_NAME)) {
                    const confirmed = await confirmDiscardChanges();
                    if (!confirmed) {
                        return false;
                    }
                    setComponentHasChanges(AUTOMATION_OPTIONS_COMPONENT_NAME, false);
                }
                return true;
            }}
        />
    );
};

interface IDataFetchWrapperProps {
    studyRef: string;
    studyNumber: string;
}

const DataFetchWrapper = ({ studyRef, studyNumber }: IDataFetchWrapperProps) => {
    const content = <Content studyRef={studyRef} studyNumber={studyNumber} />;

    return <div className="dataset-hub">{content}</div>;
};

const ViewDatasets = () => {
    const { t } = useTranslation('cm', {
        keyPrefix: 'datasetHub',
    });

    const { state } = useLocalState();

    if (!state) {
        return <ErrorComponent error={t('unexpectedError')} />;
    }

    const study = state.selectedStudy;

    if (!study) {
        return <span>{t('pleaseSelectStudy')}</span>;
    }

    return <DataFetchWrapper studyRef={study.id as string} studyNumber={study.studyNumber as string} />;
};

export default ViewDatasets;
